import Axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  isAxiosError,
} from "axios";
import { useCookie } from "@/composables";
import { useAuthSession } from "@/composables/useAuthSession";
import { AUTH_TOKEN_COOKIE, LOGIN_PATH } from "@/constants/auth";
import { buildLoginPath } from "@/utils/authRedirect";
import { AuthSignOutReason } from "@/types/auth";
import type { BodyParams, ErrorData, QueryParams } from "@/api/types";
import { transformKeysToCamel, transformKeysToSnake } from "@/api/utils";
// @ts-ignore
import qs from "qs";
import { useSnackbarStore } from "@/store/snackbar.store";
import { generateId } from "@/utils/generateId";

const BASE_URL: string = location.protocol + "//" + location.host + "/api/v1";

export class Response<T> {
  data?: T;
  errorData?: ErrorData;
  status: number | null = null;
  error: AxiosError | null = null;
  success: boolean = false;

  constructor(
    axiosResponse: AxiosResponse<T> | null = null,
    error: AxiosError | null = null,
    success: boolean = false,
  ) {
    const { showSnackbarError } = useSnackbarStore();

    this.data = axiosResponse?.data;
    this.status = axiosResponse?.status ?? null;
    this.error = error;
    this.success = success;

    const responseData = error?.response?.data;

    if (responseData && typeof responseData === "object" && "detail" in responseData) {
      this.errorData = { detail: responseData.detail, status: this.status };
    }

    if (this.status && this.status >= 500) {
      showSnackbarError("Internal server error. Please try again later.");
    }
  }

  static unknownError<T = never>(): Response<T> {
    return new Response<T>(null, null, false);
  }
}

export class ApiClient {
  private api: AxiosInstance;
  private tokenStorage = useCookie(AUTH_TOKEN_COOKIE);
  private isSigningOut: boolean = false;

  constructor() {
    this.api = Axios.create({
      baseURL: BASE_URL,
      timeout: 30000,
      headers: { "Content-Type": "application/json" },
    });

    // load saved token
    const token = this.getToken();

    if (token) {
      this.setToken(token);
    }

    this.api.interceptors.request.use((request) => {
      request.headers.set("X-Request-ID", generateId());

      if (request.url) {
        request.url = this.transformQueryParams(request.url);
      }
      if (request.data) {
        this.removeVirtualProps(request.data);
        request.data = transformKeysToSnake(request.data);
      }
      return request;
    });

    this.api.interceptors.response.use(
      (response) => {
        if (response.data) {
          response.data = transformKeysToCamel(response.data);
        }
        return response;
      },
      async (error) => {
        if (isAxiosError(error) && error.response?.status === 401 && this.shouldRedirectToLogin()) {
          this.saveToken("");

          window.location.assign(buildLoginPath(AuthSignOutReason.SessionExpired));
        }
        throw error;
      },
    );
  }

  buildParams(params: QueryParams) {
    return transformKeysToSnake(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== null && v !== undefined && v !== "")),
    );
  }

  async delete<T>(url: string): Promise<Response<T>> {
    try {
      const response = await this.api.delete(url);
      return new Response<T>(response, null, true);
    } catch (error) {
      if (isAxiosError(error)) {
        return this.handleRequestError(error, "DELETE");
      }

      return Response.unknownError();
    }
  }

  async get<T>(
    url: string,
    params: QueryParams = {},
    config: AxiosRequestConfig | undefined = undefined,
  ): Promise<Response<T>> {
    try {
      const response = await this.api.get(url, {
        params: params,
        paramsSerializer: (params) => qs.stringify(this.buildParams(params), { arrayFormat: "repeat" }),
        ...config,
      });
      return new Response<T>(response, null, true);
    } catch (error) {
      if (isAxiosError(error)) {
        return this.handleRequestError(error, "GET");
      }

      return Response.unknownError();
    }
  }

  async patch<T>(url: string, data: BodyParams): Promise<Response<T>> {
    try {
      const response = await this.api.patch(url, data);
      return new Response<T>(response, null, true);
    } catch (error) {
      if (isAxiosError(error)) {
        return this.handleRequestError(error, "PATCH");
      }

      return Response.unknownError();
    }
  }

  async post<T>(
    url: string,
    data: BodyParams,
    config: AxiosRequestConfig | undefined = undefined,
  ): Promise<Response<T>> {
    try {
      const response = await this.api.post(url, data, config);
      return new Response<T>(response, null, true);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        return this.handleRequestError(error, "POST");
      }

      return Response.unknownError();
    }
  }

  async put<T>(url: string, data: BodyParams): Promise<Response<T>> {
    try {
      const response = await this.api.put(url, data);
      return new Response<T>(response, null, true);
    } catch (error) {
      if (isAxiosError(error)) {
        return this.handleRequestError(error, "PUT");
      }

      return Response.unknownError();
    }
  }

  async save<T>(url: string, data: BodyParams): Promise<Response<T>> {
    if (data.id) {
      return await this.put<T>(url + "/" + data.id, data);
    } else {
      return await this.post<T>(url, data);
    }
  }

  async signOut(): Promise<boolean> {
    this.isSigningOut = true;

    try {
      const resp = await this.post("sign-out", {});
      if (resp.success) {
        this.saveToken(""); // remove token
      }
      return resp.success;
    } finally {
      if (this.getToken()) {
        this.isSigningOut = false;
      }
    }
  }

  saveToken(token: string): void {
    if (token) {
      this.tokenStorage.save(token);
    } else {
      this.tokenStorage.remove();
      useAuthSession().publishLogout();
    }
    this.setToken(token);
  }

  private setToken(token: string): void {
    if (token) {
      this.api.defaults.headers.common["Authorization"] = token;
    } else {
      delete this.api.defaults.headers.common["Authorization"];
    }
  }

  getToken(): string | null {
    return this.tokenStorage.get();
  }

  getWSChatUrl(): string {
    return BASE_URL.replace("http", "ws") + "/chat/ws";
  }

  removeVirtualProps(obj: BodyParams): void {
    for (const k in obj) {
      if (typeof obj[k] == "object" && obj[k] !== null) {
        this.removeVirtualProps(obj[k]);
      } else {
        if (k[0] === "$") {
          delete obj[k];
        }
      }
    }
  }

  handleRequestError(error: AxiosError<any>, requestType: string): Response<any> {
    if (error.response) {
      console.error(
        `Error processing ${requestType} ${error.request?.responseURL} with status ${error.response.status}`,
      );
      // теперь у тебя строго типизированный error.response.data
      return new Response(error.response, error, false);
    }
    return Response.unknownError();
  }

  private shouldRedirectToLogin(): boolean {
    if (this.isSigningOut) {
      return false;
    }

    if (!this.getToken()) {
      return false;
    }

    return window.location.pathname !== LOGIN_PATH;
  }

  private transformQueryParams(url: string): string {
    const [baseUrl, paramsString] = url.split("?");
    if (!paramsString) return baseUrl;

    const searchParams = new URLSearchParams(paramsString);
    const params: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      params[key] = value;
    });

    const snakeCaseParams = transformKeysToSnake(params);
    const newQuery = new URLSearchParams(snakeCaseParams).toString();

    return `${baseUrl}?${newQuery}`;
  }
}

let sharedClient: ApiClient | null = null;

export function getApiClient(): ApiClient {
  if (!sharedClient) {
    sharedClient = new ApiClient();
  }

  return sharedClient;
}
